import React, {useCallback} from "react";
import {useErrorReporting} from "@unibuddy/error-reporting";
import {Box, Text, View} from "@unibuddy/patron";

import {
    Maybe,
    AssistantMessageFeedbackType,
    useCreateAssistantMessageFeedbackMutation,
    AssistantMessageFeedback,
} from "../../../../types/generated";
import {useAssistantConversationContext} from "../../providers/AssistantConversationProvider";
import {LikeButton} from "../../../../components/Feedback/LikeButton";
import {DislikeButton} from "../../../../components/Feedback/DislikeButton";

type ChatMessageFeedbackProps = {
    messageFeedback: Maybe<AssistantMessageFeedback>;
    messageId: string;
};

export const ChatMessageFeedback = ({messageId, messageFeedback}: ChatMessageFeedbackProps) => {
    const {reportError} = useErrorReporting();
    const [feedback] = useCreateAssistantMessageFeedbackMutation();
    const {updateAssistantMessage} = useAssistantConversationContext();
    const isLiked = messageFeedback?.type === AssistantMessageFeedbackType.Like;
    const isDisliked = messageFeedback?.type === AssistantMessageFeedbackType.Dislike;

    const handleFeedback = useCallback(
        async (feedbackType: AssistantMessageFeedbackType) => {
            updateAssistantMessage(messageId, {
                feedback: {
                    __typename: "AssistantMessageFeedback",
                    type: feedbackType,
                    freeText: null,
                },
            });

            try {
                const response = await feedback({
                    variables: {
                        messageId,
                        type: feedbackType,
                    },
                });
                if (response.errors) {
                    reportError(new Error(response.errors.toString()));
                }
            } catch (e: unknown) {
                updateAssistantMessage(messageId, {
                    feedback: messageFeedback,
                });

                reportError(e as Error);
            }
        },
        /* eslint-disable-next-line */
        [messageId],
    );

    const handleLike = useCallback(async () => {
        if (isLiked) return;
        try {
            await handleFeedback(AssistantMessageFeedbackType.Like);
        } catch (error) {
            console.error(error);
        }
    }, [handleFeedback, isLiked]);

    const handleDislike = useCallback(async () => {
        if (isDisliked) return;
        try {
            await handleFeedback(AssistantMessageFeedbackType.Dislike);
        } catch (error) {
            console.error(error);
        }
    }, [handleFeedback, isDisliked]);

    return (
        <View borderTopWidth={1} borderColor="grey300" marginTop="small" paddingTop="small">
            <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Text color="grey700" size="small">
                    Was this helpful?
                </Text>
                <Box display="flex" flexDirection="row" gap="xsmall">
                    <LikeButton onClick={handleLike} isLiked={isLiked} />
                    <DislikeButton onClick={handleDislike} isDisliked={isDisliked} />
                </Box>
            </Box>
        </View>
    );
};
